import { Injectable } from '@angular/core';

export interface RetryConfig {
  maxAttempts?: number;
  initialDelay?: number;
  maxDelay?: number;
  backoffMultiplier?: number;
}

export interface RetryResult {
  success: boolean;
  attempts: number;
  error?: any;
}

/**
 * Retry Service for handling API call retries
 * 
 * Features:
 * - Automatic retry with exponential backoff
 * - Comprehensive error logging
 * - Graceful error handling
 */
@Injectable({
  providedIn: 'root'
})
export class RetryService {
  private readonly DEFAULT_MAX_ATTEMPTS = 3;
  private readonly DEFAULT_INITIAL_DELAY = 1000; // 1 second
  private readonly DEFAULT_MAX_DELAY = 10000; // 10 seconds
  private readonly DEFAULT_BACKOFF_MULTIPLIER = 2;

  constructor() { }

  /**
   * Retry a promise-based function with exponential backoff
   */
  async retryPromise<T>(
    fn: () => Promise<T>,
    config?: RetryConfig
  ): Promise<T> {
    const maxAttempts = config?.maxAttempts || this.DEFAULT_MAX_ATTEMPTS;
    const initialDelay = config?.initialDelay || this.DEFAULT_INITIAL_DELAY;
    const maxDelay = config?.maxDelay || this.DEFAULT_MAX_DELAY;
    const backoffMultiplier = config?.backoffMultiplier || this.DEFAULT_BACKOFF_MULTIPLIER;

    let attempt = 0;
    let delay = initialDelay;
    let lastError: any;

    while (attempt < maxAttempts) {
      attempt++;
      try {
        console.log(`[RetryService] Attempt ${attempt}/${maxAttempts}`);
        const result = await fn();
        
        if (attempt > 1) {
          console.log(`[RetryService] Success on attempt ${attempt}`);
        }
        
        return result;
      } catch (error) {
        lastError = error;
        console.error(`[RetryService] Attempt ${attempt} failed:`, error);
        
        // Log error details
        this.logError(error, attempt, maxAttempts);
        
        if (attempt < maxAttempts) {
          console.log(`[RetryService] Retrying in ${delay}ms...`);
          await this.sleep(delay);
          delay = Math.min(delay * backoffMultiplier, maxDelay);
        }
      }
    }

    console.error(`[RetryService] All ${maxAttempts} attempts failed`);
    throw lastError;
  }

  /**
   * Retry a fetch-based streaming function
   * Returns a ReadableStreamDefaultReader or undefined
   */
  async retryStream(
    streamFn: () => Promise<ReadableStreamDefaultReader<Uint8Array> | undefined>,
    config?: RetryConfig
  ): Promise<ReadableStreamDefaultReader<Uint8Array> | undefined> {
    const maxAttempts = config?.maxAttempts || this.DEFAULT_MAX_ATTEMPTS;
    const initialDelay = config?.initialDelay || this.DEFAULT_INITIAL_DELAY;
    const maxDelay = config?.maxDelay || this.DEFAULT_MAX_DELAY;
    const backoffMultiplier = config?.backoffMultiplier || this.DEFAULT_BACKOFF_MULTIPLIER;

    let attempt = 0;
    let delay = initialDelay;
    let lastError: any;

    while (attempt < maxAttempts) {
      attempt++;
      try {
        console.log(`[RetryService] Stream attempt ${attempt}/${maxAttempts}`);
        const reader = await streamFn();
        
        if (reader) {
          if (attempt > 1) {
            console.log(`[RetryService] Stream successful on attempt ${attempt}`);
          }
          return reader;
        } else {
          throw new Error('Stream reader is undefined');
        }
      } catch (error) {
        lastError = error;
        console.error(`[RetryService] Stream attempt ${attempt} failed:`, error);
        
        // Log error details
        this.logError(error, attempt, maxAttempts);
        
        if (attempt < maxAttempts) {
          console.log(`[RetryService] Retrying stream in ${delay}ms...`);
          await this.sleep(delay);
          delay = Math.min(delay * backoffMultiplier, maxDelay);
        }
      }
    }

    console.error(`[RetryService] All ${maxAttempts} stream attempts failed`);
    return undefined;
  }

  /**
   * Wrap a streaming response reader with error detection and retry capability
   */
  createResilientStreamReader(
    reader: ReadableStreamDefaultReader<Uint8Array>,
    onError: (error: any) => void,
    onRetry: () => Promise<ReadableStreamDefaultReader<Uint8Array> | undefined>
  ): ReadableStreamDefaultReader<Uint8Array> {
    // Create a proxy that wraps the reader's read method
    const originalRead = reader.read.bind(reader);
    let retryCount = 0;
    const maxRetries = 2; // Allow 2 additional retries

    reader.read = async function() {
      try {
        return await originalRead();
      } catch (error) {
        console.error('[RetryService] Stream read error:', error);
        
        if (retryCount < maxRetries) {
          retryCount++;
          console.log(`[RetryService] Attempting to recover stream (retry ${retryCount}/${maxRetries})`);
          
          try {
            const newReader = await onRetry();
            if (newReader) {
              // Replace the reader
              return newReader.read();
            }
          } catch (retryError) {
            console.error('[RetryService] Stream recovery failed:', retryError);
          }
        }
        
        onError(error);
        throw error;
      }
    };

    return reader;
  }

  /**
   * Check if an error is retryable
   */
  isRetryableError(error: any): boolean {
    // Network errors are retryable
    if (error instanceof TypeError && error.message.includes('Failed to fetch')) {
      return true;
    }
    
    // Timeout errors are retryable
    if (error.name === 'AbortError' || error.message?.includes('timeout')) {
      return true;
    }
    
    // HTTP 5xx errors are retryable
    if (error.status >= 500 && error.status < 600) {
      return true;
    }
    
    // 429 (Too Many Requests) is retryable
    if (error.status === 429) {
      return true;
    }
    
    return false;
  }

  /**
   * Log error details for debugging
   */
  private logError(error: any, attempt: number, maxAttempts: number): void {
    const errorLog = {
      timestamp: new Date().toISOString(),
      attempt: attempt,
      maxAttempts: maxAttempts,
      errorType: error.name || typeof error,
      errorMessage: error.message || String(error),
      errorStatus: error.status,
      errorDetails: error
    };
    
    console.error('[RetryService] Error details:', errorLog);
    
    // In production, you might want to send this to an error tracking service
    // like Sentry, LogRocket, or Application Insights
  }

  /**
   * Sleep utility for delays
   */
  private sleep(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * Create an error message for the user
   */
  createUserFriendlyErrorMessage(attempts: number): string {
    return `We encountered an issue connecting to the AI service after ${attempts} attempts. Your chat session is still active - please try your request again.`;
  }
}

